import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { getAuthHeaders } from '../utils/authHelper';
import './BoardManagePage.css';

const BoardManagePage = () => {
  const { user } = useAuth();
  const { getServer } = useContext(ServerConfigContext);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    category: 'general',
    description: '',
    icon: '',
    min_read_grade: 'newbie',
    min_write_grade: 'newbie'
  });

  const gradeOptions = [
    { value: 'newbie', label: '뉴비' },
    { value: 'bronze', label: '브론즈' },
    { value: 'silver', label: '실버' },
    { value: 'gold', label: '골드' },
    { value: 'platinum', label: '플래티넘' },
    { value: 'diamond', label: '다이아몬드' }
  ];

  // board-api-server URL 생성 (프록시 사용)
  const getApiUrl = (endpoint) => {
    return `/board-api/api/v1${endpoint}`;
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/boards'), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`게시판 목록 조회 실패: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ [BoardManagePage] API 응답:', result);
      
      if (result.success && result.data) {
        // 실제 API 응답: data는 배열이고, 각 원소의 boards 속성에 게시판 배열이 있음
        // boards가 null일 수 있는 예외상황 처리
        const allBoards = [];
        result.data.forEach(item => {
          // item.boards가 null이 아니고 배열인 경우에만 처리
          if (item.boards && Array.isArray(item.boards)) {
            allBoards.push(...item.boards);
          } else {
            console.log('ℹ️ [BoardManagePage] 빈 카테고리:', item.title || 'Unknown', '- boards:', item.boards);
          }
        });
        console.log('✅ [BoardManagePage] 추출된 게시판 목록:', allBoards);
        setBoards(allBoards);
      } else {
        console.warn('⚠️ [BoardManagePage] 응답 데이터 없음:', result);
        setBoards([]);
      }
      setError('');
    } catch (err) {
      console.error('게시판 목록 조회 오류:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingBoard ? getApiUrl(`/boards/${editingBoard.id}`) : getApiUrl('/boards');
      const method = editingBoard ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `게시판 ${editingBoard ? '수정' : '생성'} 실패`);
      }

      await fetchBoards();
      resetForm();
      setError('');
      
      console.log(`✅ 게시판 ${editingBoard ? '수정' : '생성'} 성공`);
    } catch (err) {
      console.error('게시판 저장 오류:', err);
      setError(err.message);
    }
  };

  const handleEdit = (board) => {
    setEditingBoard(board);
    setFormData({
      id: board.id,
      name: board.name,
      category: board.category,
      description: board.description || '',
      icon: board.icon || '',
      min_read_grade: board.min_read_grade,
      min_write_grade: board.min_write_grade
    });
    setShowAddForm(true);
  };

  const handleDelete = async (boardId, boardName) => {
    if (!window.confirm(`"${boardName}" 게시판을 삭제하시겠습니까?\n\n⚠️ 게시글이 있는 게시판은 삭제할 수 없습니다.`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/boards/${boardId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === 'BOARD_HAS_POSTS') {
          throw new Error('게시글이 있는 게시판은 삭제할 수 없습니다. 먼저 모든 게시글을 삭제해주세요.');
        }
        throw new Error(errorData.message || '게시판 삭제 실패');
      }

      await fetchBoards();
      setError('');
      console.log(`✅ 게시판 "${boardName}" 삭제 성공`);
    } catch (err) {
      console.error('게시판 삭제 오류:', err);
      setError(err.message);
    }
  };

  // 순서 변경 API 호출 함수
  const handleOrderChange = async (boardId, newOrder) => {
    if (!newOrder || newOrder < 1) {
      setError('순서는 1 이상의 숫자여야 합니다.');
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/boards/${boardId}/order`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ display_order: parseInt(newOrder) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '순서 변경 실패');
      }

      // 순서 변경 후 게시판 목록 다시 조회
      await fetchBoards();
      setError('');
      console.log(`✅ 게시판 ID ${boardId} 순서 변경 성공: ${newOrder}`);
    } catch (err) {
      console.error('순서 변경 오류:', err);
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      category: 'general',
      description: '',
      icon: '',
      min_read_grade: 'newbie',
      min_write_grade: 'newbie'
    });
    setShowAddForm(false);
    setEditingBoard(null);
  };

  const getGradeLabel = (grade) => {
    const option = gradeOptions.find(opt => opt.value === grade);
    return option ? option.label : grade;
  };

  if (loading) {
    return (
      <div className="board-management-page">
        <div className="loading-message">📋 게시판 목록을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="board-management-page">
      <div className="page-header">
        <h2>📋 게시판 관리</h2>
        <button 
          className="add-board-button"
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          ➕ 새 게시판 추가
        </button>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {showAddForm && (
        <div className="board-form-container">
          <form onSubmit={handleSubmit} className="board-form">
            <h3>{editingBoard ? '게시판 수정' : '새 게시판 추가'}</h3>
            
            <div className="form-group">
              <label htmlFor="category">카테고리</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                required
              >
                <option value="general">일반</option>
                <option value="development">개발</option>
                <option value="hobby">취미</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="name">게시판 이름</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="예: 자유게시판, Q&A, 공지사항"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="icon">아이콘</label>
              <input
                type="text"
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                placeholder="예: 💬, 📋, 🎮"
                maxLength="10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">설명</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="게시판에 대한 간단한 설명"
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="min_read_grade">읽기 권한</label>
                <select
                  id="min_read_grade"
                  value={formData.min_read_grade}
                  onChange={(e) => setFormData({...formData, min_read_grade: e.target.value})}
                >
                  {gradeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="min_write_grade">쓰기 권한</label>
                <select
                  id="min_write_grade"
                  value={formData.min_write_grade}
                  onChange={(e) => setFormData({...formData, min_write_grade: e.target.value})}
                >
                  {gradeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-button">
                취소
              </button>
              <button type="submit" className="submit-button">
                {editingBoard ? '수정' : '추가'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="boards-table-container">
        {boards.length === 0 ? (
          <div className="empty-message">📋 등록된 게시판이 없습니다.</div>
        ) : (
          <table className="boards-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>순서</th>
                <th>카테고리</th>
                <th>게시판 이름</th>
                <th>설명</th>
                <th>읽기 권한</th>
                <th>쓰기 권한</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {boards.map(board => (
                <tr key={board.id}>
                  <td className="board-id">{board.id}</td>
                  <td className="board-order">
                    <input
                      type="number"
                      min="1"
                      max="999"
                      className="order-input"
                      defaultValue={board.display_order || 1}
                      onBlur={(e) => {
                        const newOrder = parseInt(e.target.value);
                        if (newOrder !== (board.display_order || 1) && newOrder >= 1) {
                          handleOrderChange(board.id, newOrder);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                      disabled={showAddForm}
                    />
                  </td>
                  <td className="board-category">{board.category}</td>
                  <td className="board-name">
                    {board.icon && <span className="board-icon">{board.icon}</span>}
                    {board.name}
                  </td>
                  <td className="board-description">{board.description || '-'}</td>
                  <td className="permission-grade">
                    <span className={`grade-badge grade-${board.min_read_grade}`}>
                      {getGradeLabel(board.min_read_grade)}
                    </span>
                  </td>
                  <td className="permission-grade">
                    <span className={`grade-badge grade-${board.min_write_grade}`}>
                      {getGradeLabel(board.min_write_grade)}
                    </span>
                  </td>
                  <td className="actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleEdit(board)}
                      disabled={showAddForm}
                    >
                      ✏️ 수정
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDelete(board.id, board.name)}
                      disabled={showAddForm}
                    >
                      🗑️ 삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BoardManagePage;